import type { Settings } from "../../shared/api.js";
import { Page } from "../Page.js";
import { h } from "../dom.js";

/** App settings, including how many CPUs stay free for manual crafts. */
export class SettingsPage extends Page {
  readonly title = "Settings";

  async mount(): Promise<void> {
    const settings = await this.attempt(() => this.ctx.api.settings());
    if (!settings) return;
    const reserved = h("input", { type: "number", min: "0", value: String(settings.reservedCpus) });
    const interval = h("input", { type: "number", min: "1", value: String(settings.decisionInterval) });
    const critical = h("input", { type: "number", min: "0", value: String(settings.maxCriticalRules) });
    const auto = h("input", { type: "checkbox", checked: settings.autoCraft });
    const power = h("input", { type: "checkbox", checked: settings.prioritizePower });
    const monitorAll = h("input", { type: "checkbox", checked: settings.monitorAll });
    const flowDays = h("input", { type: "number", min: "1", max: "365", value: String(settings.flowDays) });
    const form = h(
      "form",
      {
        class: "card settings",
        onsubmit: async (e: Event) => {
          e.preventDefault();
          const change: Partial<Settings> = {
            reservedCpus: Number(reserved.value),
            decisionInterval: Number(interval.value),
            maxCriticalRules: Number(critical.value),
            autoCraft: auto.checked,
            prioritizePower: power.checked,
            monitorAll: monitorAll.checked,
            flowDays: Number(flowDays.value),
          };
          await this.attempt(() => this.ctx.api.updateSettings(change), "Settings saved");
        },
      },
      h("h2", null, "Automatic crafting"),
      h("label", { class: "check" }, auto, "Craft automatically from the rules"),
      h("label", null, "CPUs kept free for manual crafts", reserved, h("span", { class: "muted small" }, "Rules use the smallest free CPUs first, so the biggest ones are the ones kept free.")),
      h("label", null, "Seconds between decision passes", interval, h("span", { class: "muted small" }, "A stock drop below min also triggers a pass straight away.")),
      h("label", { class: "check" }, power, "Prioritise power when AE plans a craft"),
      h("h2", null, "Home page"),
      h(
        "label",
        { class: "check" },
        monitorAll,
        "Monitor everything the network stores",
      ),
      h("span", { class: "muted small" }, "Every stored item, fluid and essentia shows on the home page with its stock history, read from the game every 5 minutes. It is only charted, never crafted. Turn it off on a big network and pick resources with \"Monitor a resource\" instead."),
      h(
        "label",
        null,
        "Days every stock change is kept",
        flowDays,
        h("span", { class: "muted small" }, "For the Flows page. Each change of a watched resource is one row; longer windows use hourly totals, kept a year. A big, busy network writes a lot of changes, so keep this short there."),
      ),
      h("h2", null, "Offline stocking"),
      h("label", null, "Critical rules copied to the computer", critical, h("span", { class: "muted small" }, "Highest priority first. The computer stocks these on its own while the app is unreachable.")),
      h("div", { class: "row" }, h("span", { class: "spacer" }), h("button", { class: "primary", type: "submit" }, "Save")),
    );
    this.el.append(
      form,
      h(
        "div",
        { class: "card" },
        h("h2", null, "Session"),
        h("button", { onclick: async () => { await this.attempt(() => this.ctx.api.logout()); location.reload(); } }, "Log out"),
      ),
    );
  }
}
